import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Lock, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Discreet, near-invisible entry to the private creation studio.
// Lives in the lower-left corner — same tone as the page, only the cursor gives it away.
// Two-step gate: a personal security question, then the admin passcode.
const SECURITY_QUESTION = "What is my cat's name?";
const SECURITY_ANSWER = "pepper";

export default function AdminGate() {
  const navigate = useNavigate();
  const { user, adminLogin } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("question"); // question | passcode
  const [answer, setAnswer] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const start = () => {
    if (user?.unlimited) { navigate("/studio"); return; }
    setStep("question");
    setAnswer("");
    setCode("");
    setOpen(true);
  };

  const close = () => { setOpen(false); setAnswer(""); setCode(""); setStep("question"); };

  const checkAnswer = () => {
    if (answer.trim().toLowerCase() === SECURITY_ANSWER) {
      setStep("passcode");
    } else {
      toast.error("That's not it.");
    }
  };

  const submitCode = async () => {
    setBusy(true);
    try {
      await adminLogin(code);
      toast.success("Welcome back to the Studio 🎬");
      close();
      navigate("/studio");
    } catch {
      toast.error("Incorrect passcode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Invisible hotspot — cursor turns to a pointer, nothing else visible */}
      <button
        data-testid="admin-gate-btn"
        aria-label=" "
        onClick={start}
        className="fixed bottom-0 left-0 w-14 h-14 z-50 cursor-pointer bg-transparent border-0 p-0 m-0"
        style={{ outline: "none" }}
      />

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={close}
          data-testid="admin-gate-overlay"
        >
          <div
            className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden border border-[#e7defb]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 bg-[#171426] text-white">
              <div className="flex items-center gap-2 font-extrabold">
                {step === "question" ? <Lock size={18} className="text-[#a9c39c]" /> : <KeyRound size={18} className="text-[#a9c39c]" />}
                {step === "question" ? "Verify it's you" : "Studio access"}
              </div>
              <button data-testid="admin-gate-close" onClick={close} className="p-1 rounded-full hover:bg-white/10"><X size={20} /></button>
            </div>

            <div className="px-5 py-5">
              {step === "question" ? (
                <>
                  <label className="block text-sm font-bold text-[#2c2841] mb-2">{SECURITY_QUESTION}</label>
                  <input
                    data-testid="admin-gate-answer"
                    autoFocus
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && checkAnswer()}
                    placeholder="Your answer"
                    className="sms-input w-full px-4 py-2.5"
                  />
                  <button
                    data-testid="admin-gate-answer-submit"
                    onClick={checkAnswer}
                    className="sms-solid-btn w-full text-white font-bold py-3 rounded-2xl mt-3"
                  >
                    Continue
                  </button>
                </>
              ) : (
                <>
                  <label className="block text-sm font-bold text-[#2c2841] mb-2">Enter your admin passcode</label>
                  <input
                    data-testid="admin-gate-passcode"
                    type="password"
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !busy && submitCode()}
                    placeholder="Passcode"
                    className="sms-input w-full px-4 py-2.5 text-center tracking-widest"
                  />
                  <button
                    data-testid="admin-gate-passcode-submit"
                    onClick={submitCode}
                    disabled={busy}
                    className="sms-solid-btn w-full text-white font-bold py-3 rounded-2xl mt-3 disabled:opacity-70"
                  >
                    {busy ? "Unlocking…" : "Enter Studio"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
